import { Portal } from '@chakra-ui/react/portal';
import { Tooltip, type TooltipRootProps } from '@chakra-ui/react/tooltip';
import { type ReactNode, type Ref, useContext, useRef } from 'react';

import BitkitPortalContext from '../../utilities/BitkitPortalContext';

export type BitkitLabelTooltipProps = {
  /** The control the tooltip labels. Rendered via `asChild`, so it must accept a ref. */
  children: ReactNode;
  /** Render the trigger without a tooltip — for labels that only apply in some states. */
  disabled?: TooltipRootProps['disabled'];
  /**
   * Render the tooltip inline instead of in a portal. Only needed when it must stay inside its
   * parent's DOM subtree — a dialog already sets this for you.
   */
  disablePortal?: boolean;
  /** Where the tooltip sits relative to the trigger (default `top`). It flips if space is short. */
  placement?: NonNullable<TooltipRootProps['positioning']>['placement'];
  /** The label. Keep it to a couple of words — this is a name, not an explanation. */
  text: string;
};

/**
 * Names a control that shows no text of its own, typically an icon button. Small, tight and
 * semibold, in contrast to `BitkitTooltip`, which explains rather than labels.
 *
 * Note this only *describes* the trigger to sighted users — it does not give it an accessible name.
 * An icon-only control still needs its own `aria-label`.
 *
 * The content renders in a portal so it escapes ancestors that trap stacking or clip overflow.
 * Dialogs turn that off through `BitkitPortalContext`; `disablePortal` is the manual override.
 */
const BitkitLabelTooltip = ({
  children,
  disablePortal,
  disabled,
  placement = 'top',
  text,
}: BitkitLabelTooltipProps) => {
  const { disablePortal: disablePortalFromContext } = useContext(BitkitPortalContext);

  // Anchor positioning to the trigger node by ref rather than relying on Zag's
  // id/data-scope lookup. When the trigger is also wrapped by another `asChild`
  // trigger (e.g. Menu.Trigger, Dialog.CloseTrigger), the outer trigger's id and
  // data-scope/data-part overwrite ours on the shared node, so `getTriggerEl()`
  // can't find it and the tooltip loses its anchor. `getAnchorElement` takes
  // precedence over the id lookup, so this is safe in the standalone case too.
  const triggerRef = useRef<HTMLElement>(null);

  return (
    <Tooltip.Root
      disabled={disabled}
      paddingSize="sm"
      positioning={{ getAnchorElement: () => triggerRef.current, placement, offset: { mainAxis: 4, crossAxis: 0 } }}
      textStyle="sm/semibold"
    >
      <Tooltip.Trigger ref={triggerRef as Ref<HTMLButtonElement>} asChild>
        {children}
      </Tooltip.Trigger>
      <Portal disabled={disablePortal || disablePortalFromContext}>
        <Tooltip.Positioner>
          <Tooltip.Content>{text}</Tooltip.Content>
        </Tooltip.Positioner>
      </Portal>
    </Tooltip.Root>
  );
};

BitkitLabelTooltip.displayName = 'BitkitLabelTooltip';

export default BitkitLabelTooltip;
